<template lang="pug">
  .hcb-gmap(v-if="locationsLoaded")
    GoogleMap.map(
      ref="map"
      :api-promise="$gmapApiPromiseLazy()"
      :center="center"
      :zoom="zoom"
      :disable-default-ui="true"
      :zoom-control="true"
      :fullscreen-control="true"
      :min-zoom="3"
      :max-zoom="14"
      :map-id="'e54caf4b0aa248c184cb905c'"
      :style="mapDimensions"
    )
      AdvancedMarker(
        :key="marker.location.code"
        v-for="marker in markers"
        :options="marker.options"
        @click="onMarkerClicked(marker)"
      )
        InfoWindow(
          v-if="marker.infoWindowOpened"
          :model-value="marker.infoWindowOpened"
          :options="marker.infoWindowOptions"
          :position="marker.position"
          @closeclick="marker.infoWindowOpened = false"
        )
          MarkerInfoWindow(:location="marker.location")

      AdvancedMarker(
        v-if="currentPlace"
        :options="{ position: currentPlace.position }"
      )

</template>

<script>
  /* globals MR */
  import { utilities } from '@gmap-vue/v3';
  import { GoogleMap, AdvancedMarker, InfoWindow } from "vue3-google-map";

  import MarkerInfoWindow from '@components/ColorBarMapSection/MarkerInfoWindow';

  import { mapState, mapMutations, mapActions } from 'vuex';

  import throttle from 'lodash.throttle';
  
  const { getGoogleMapsAPI, googleMapsApiInitializer } = utilities;
  
  const UNITED_STATES_CENTER = {
    lat: 39.8097343,
    lng: -98.5556199,
  };
  const DEFAULT_ZOOM_LEVEL = 3;
  const MAX_MARKERS_DISTANCE_FROM_CENTER_IN_MILES = 60;
  const KMS_TO_MILES_COEFFICIENT = 0.000621371 * 1000;

  export default {
    name: "HcbGmap",

    components: {
      MarkerInfoWindow,
      GoogleMap,
      AdvancedMarker,
      InfoWindow
    },

    props: {
      startLocation: {
        type: Object,
        default: null
      },
      nearbyLocationsMaxDistance: {
        type: Number,
        default: MAX_MARKERS_DISTANCE_FROM_CENTER_IN_MILES,
      }
    },

    data() {
      return {
        center: UNITED_STATES_CENTER,
        zoom: DEFAULT_ZOOM_LEVEL,
        locationsLoaded: false,
        startMapLocation: null,
        selectedMarker: null,
        currentPlace: null,
        markers: [],
      };
    },

    computed: {
      ...mapState('global', ['windowData']),
      ...mapState('colorbar', ['mapLocations', 'nearbyMapLocations', 'selectedMapLocation']),
      gmapApi: getGoogleMapsAPI,

      isMapReady() {
        return this.locationsLoaded && this.$refs.map?.ready && this.markers.length > 0;
      },

      isMobile() {
        return this.windowData.breakpoint === 'mobile';
      },

      mapDimensions() {
        if (!this.isMobile) {
          return {
            width: '100%',
            height: '450px',
          };
        }

        return {
          width: '100%',
          height: '370px'
        };
      },

    },

    watch: {
      mapLocations: {
        handler() {
          if (!this.mapLocations) {
            return;
          }

          this.markers = this.mapLocations.map(location => ({
            location,
            position: location.position,
            infoWindowOpened: false,
            options: {
              title: location.name,
              position: location.position,
              content: this.createMarkerIcon(),
            },
            infoWindowOptions: {
              maxWidth: 384,
              pixelOffset: {
                width: 0,
                height: -1,
              },
            },
          }));
        },
        deep: true,
      },

      isMapReady() {
        this.$nextTick(() => {
          if (this.startLocation) {
            this.startMapLocation = this.mapLocations.find(location => location.code == this.startLocation.code);

            if (this.startMapLocation) {
              this.setNearbyLocations(this.startMapLocation.position);
              this.setSelectedMapLocation(this.startMapLocation);

              if (this.nearbyMapLocations) {
                this.$nextTick(() => {
                  this.selectedMarker = this.markers.find(marker => marker.location.code == this.startMapLocation.code);

                  let bounds = new this.gmapApi.maps.LatLngBounds();
                  this.nearbyMapLocations.forEach(({ position }) => {
                    bounds.extend(position);
                  });
                  bounds.extend(this.startMapLocation.position);

                  this.$nextTick(() => {
                    this.$refs.map.map.fitBounds(bounds);
                  });
                });
              }
            }
          }
        });
      },
      mapDimensions() {
        this.resizeMap();
      },

      selectedMapLocation() {
        this.selectLocation(this.selectedMapLocation);
      }
    },

    async mounted() {
      let config = this.$root.MRConfig || window?.MR?.config || {};
      googleMapsApiInitializer({
        key: config.googleAutocomplete,
        libraries: 'places,geometry',
        v: 'weekly',
      });

      await this.$gmapApiPromiseLazy();
      
      await this.gmapApi.maps.importLibrary('maps');
      await this.gmapApi.maps.importLibrary('marker');

      await this.getActiveLocationsListForMapView();

      this.locationsLoaded = true;
    },

    methods: {
      ...mapActions('colorbar', ['getActiveLocationsListForMapView']),
      ...mapMutations('colorbar', ['setNearbyMapLocations', 'setSelectedMapLocation']),

      createMarkerIcon() {
        const width = 20;
        const glyphImg = document.createElement('img');
        glyphImg.src = new URL('https://media.madison-reed.com/d3ewrnwdcmri66.cloudfront.net/content/images/2022/2/l1cm1wy8-map-marker-2/map-marker-2.png').href;
        
        glyphImg.style.width = `${width}px`;
        glyphImg.style.height = `${width * 1.25}px`;
        
        return glyphImg;
      },

      onMarkerClicked(marker) {
        if (this.selectedMarker?.infoWindowOpened) {
          this.selectedMarker.infoWindowOpened = false;
        }
        this.trackEvent(`Map Marked Clicked`, { marker });
        this.selectedMarker = marker;
        this.selectedMarker.infoWindowOpened = true;
        if (marker.location) {
          this.setSelectedMapLocation(marker.location);
        }
      },

      setNearbyLocations(position) {
        let nearbyLocations = [];

        this.mapLocations.forEach(location => {
          let distanceInMeters = this.gmapApi.maps.geometry.spherical.computeDistanceBetween(position,location.position);
          let distanceInMiles = (distanceInMeters / 1000) * KMS_TO_MILES_COEFFICIENT;

          if (distanceInMiles > this.nearbyLocationsMaxDistance) {
            return;
          }

          nearbyLocations.push({
            ...location,
            distance: distanceInMiles,
          });
        });

        this.setNearbyMapLocations(nearbyLocations);
      },

      selectLocation(location) {
        const marker = this.markers.find(marker => marker.location.code == location.code);
        if (marker) {
          this.onMarkerClicked(marker);
        }
      },

      resizeMap: throttle(function () {
        this.gmapApi?.maps?.event?.trigger(this.$refs.map?.map, 'resize');
      }, 300),
    },
  };
</script>

<style scoped lang="stylus">
</style>
